import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Input, Button, Switch, Select } from 'antd';
import DetailModal from 'components/Basic/DetailModal';
import { ArrowForward, ArrowDropDown, Store } from '@material-ui/icons';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import toast from 'components/Basic/Toast';
import { useAppDispatch, useAppSelector } from 'utilities/redux';
import {
  useCreateEstablishmentsMutation,
  useUpdateEstablishmentsMutation,
} from 'core/worldCup/establishments/EstablishmentsService';
import { Establishments } from 'core/worldCup/establishments/types';
import { sanitizeRule } from 'utilities/sanitize';
import { useLazyGetSponsorsSimpleListQuery } from 'core/worldCup/sponsors/SponsorsService';
import { setSponsorsData } from 'core/worldCup/sponsors/SponsorsSlice';
import TextArea from 'antd/es/input/TextArea';
import { DEFAULT_SORT_QUERY } from 'utilities/constants';
import { getApiErrorMessage } from 'utilities/getApiErrorMessages';

type EstablishmentFormData = {
  sponsorId: number;
  name: string;
  description?: string;
  address?: string;
  phone?: string;
  isActive: boolean;
};

type EstablishmentsFormModalType = {
  mode: 'modal' | 'fullscreen';
  isOpenModal: boolean;
  establishment: Establishments | null;
  onCloseModal: () => void;
  onSaveSuccess: () => void;
};

const EstablishmentsFormModal: React.FC<EstablishmentsFormModalType> = ({
  mode,
  isOpenModal,
  establishment,
  onCloseModal,
  onSaveSuccess,
}) => {
  const [form] = Form.useForm<EstablishmentFormData>();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const isModal = mode === 'modal';

  const [phoneValue, setPhoneValue] = useState('');

  const [createEstablishment, { isLoading: isCreating }] = useCreateEstablishmentsMutation();
  const [updateEstablishment, { isLoading: isUpdating }] = useUpdateEstablishmentsMutation();

  const [getSponsorsSimpleList] = useLazyGetSponsorsSimpleListQuery();
  const sponsors = useAppSelector(state => state.sponsorsReducer).list.data || [];

  const selectClassName = isModal ? 'select_modal' : 'select_form';

  useEffect(() => {
    getInitialData();
  }, []);

  useEffect(() => {
    if (isOpenModal) {
      if (establishment) {
        form.setFieldsValue({
          sponsorId: establishment.sponsorId || establishment.sponsor?.id || undefined,
          name: establishment.name || '',
          description: establishment.description || undefined,
          address: establishment.address,
          phone: establishment.phone,
          isActive: establishment.isActive
        });
        setPhoneValue(establishment.phone || '');
      } else {
        form.resetFields();
        setPhoneValue('');
      }
    }
  }, [isOpenModal, establishment, form]);

  const getInitialData = async () => {
    const queryString = DEFAULT_SORT_QUERY;

    try {
      const res = await getSponsorsSimpleList({ queryString }).unwrap();
      dispatch(setSponsorsData({ list: res }));
    } catch (error) {
      console.error('Error fetching sponsors list:', error);
    } finally {
    }
  };


  const handleFinish = async (values: EstablishmentFormData) => {
    try {
      const body = {
        sponsorId: values.sponsorId,
        name: values.name.trim(),
        description: values.description || '',
        address: values.address || '',
        phone: phoneValue || '',
        isActive: establishment ? values.isActive : true,
      };

      if (establishment && establishment.id) {
        await updateEstablishment({ id: establishment.id, data: body }).unwrap();
        toast.success({ title: intl.formatMessage({ id: 'Establishment is updated successfully!' }) });
      } else {
        await createEstablishment(body).unwrap();
        toast.success({ title: intl.formatMessage({ id: 'Establishment is created successfully!' }) });
      }

      onCloseModal();
      onSaveSuccess();

    } catch (e: any) {
      console.error('Establishment save error:', e);
      const defaultMsg = intl.formatMessage({ id: establishment?.id ? 'Updating establishment is failure!' : 'Creating establishment is failure!' });
      toast.error({ title: getApiErrorMessage(e, intl.locale, defaultMsg) });
    }
  };

  const isEditMode = establishment && establishment.id;

  const renderFormContent = () => (
    <Form
      form={form}
      className={isModal ? 'basic_info_form' : 'establishments_form'}
      onFinish={handleFinish}
      initialValues={{ name: null, description: null, sponsorId: undefined, isActive: true }}
    >
      {isModal ? (
        <>
          <div className="establishments_input">
            <p className="title"><FormattedMessage id="Sponsor" /></p>
            <Form.Item name="sponsorId" rules={[{ required: true, message: intl.formatMessage({ id: 'Field is required' }) }]}>
              <Select
                className="select_modal"
                suffixIcon={<ArrowDropDown />}
                id="sponsor_id_filter"
                dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
              >
                {sponsors &&
                  sponsors.map((item: any) => (
                    <Select.Option value={item.id} key={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </div>
          <div className="establishments_input">
            <p className="title"><FormattedMessage id="Name" /></p>
            <Form.Item name="name" rules={[{ required: true, message: intl.formatMessage({ id: 'Name is required' }) }, sanitizeRule(intl.formatMessage({ id: 'Invalid characters detected' }))]}>
              <Input placeholder={intl.formatMessage({ id: 'Name' })} maxLength={200} />
            </Form.Item>
          </div>
          <div className="establishments_input">
            <p className="title"><FormattedMessage id="Description" /></p>
            <Form.Item name="description" rules={[sanitizeRule(intl.formatMessage({ id: 'Invalid characters detected' }))]}>
              <TextArea rows={4} className="textarea_modal" placeholder={intl.formatMessage({ id: 'Description' })} maxLength={200} />
            </Form.Item>
          </div>
          <div className="establishments_input">
            <p className="title"><FormattedMessage id="Phone" /></p>
            <div className="phone_wrapper">
              <ReactPhoneInput
                country="gt"
                value={phoneValue}
                onChange={(value: string) => setPhoneValue(value)}
              />
            </div>
          </div>
          <div className="establishments_input">
            <p className="title"><FormattedMessage id="Address" /></p>
            <Form.Item name="address" rules={[sanitizeRule(intl.formatMessage({ id: 'Invalid characters detected' }))]}>
              <TextArea rows={4} className="textarea_modal" placeholder={intl.formatMessage({ id: 'Address' })} maxLength={200} />
            </Form.Item>
          </div>
          <div className={isEditMode ? 'side_btns' : 'side_btns_camp'}>
            {isEditMode && (
              <div className="aditional_detail">
                <p><FormattedMessage id="Active" /></p>
                <Form.Item name="isActive" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
              </div>
            )}
            <div className="btn_wrapper">
              <Button className="save_sar_btn" htmlType="submit" loading={isCreating || isUpdating}>
                <FormattedMessage id={isEditMode ? 'Save changes' : 'Save'} />
                <ArrowForward />
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="establishments_form_fields">
            <div className="establishments_form_row">
              <div className="establishments_field">
                <p className="title"><FormattedMessage id="Sponsor" /></p>
                <Form.Item name="sponsorId" rules={[{ required: true, message: intl.formatMessage({ id: 'Field is required' }) }]}>
                  <Select
                    className="select_form"
                    suffixIcon={<ArrowDropDown />}
                    dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
                  >
                    {sponsors &&
                      sponsors.map((item: any) => (
                        <Select.Option value={item.id} key={item.id}>
                          {item.name}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
              </div>
            </div>
            <div className="establishments_form_row">
              <div className="establishments_field">
                <p className="title"><FormattedMessage id="Name" /></p>
                <Form.Item name="name" rules={[{ required: true, message: intl.formatMessage({ id: 'Name is required' }) }, sanitizeRule(intl.formatMessage({ id: 'Invalid characters detected' }))]}>
                  <Input placeholder={intl.formatMessage({ id: 'Name' })} maxLength={200} />
                </Form.Item>
              </div>
            </div>
            <div className="establishments_form_row">
              <div className="establishments_field">
                <p className="title"><FormattedMessage id="Description" /></p>
                <Form.Item name="description">
                  <TextArea rows={4} className="textarea_form" placeholder={intl.formatMessage({ id: 'Description' })} maxLength={200} />
                </Form.Item>
              </div>
            </div>
            <div className="establishments_form_row">
              <div className="establishments_field">
                <p className="title"><FormattedMessage id="Phone" /></p>
                <div className="phone_wrapper">
                  <ReactPhoneInput
                    country="gt"
                    value={phoneValue}
                    onChange={(value: string) => setPhoneValue(value)}
                  />
                </div>
              </div>
            </div>
            <div className="establishments_form_row">
              <div className="establishments_field">
                <p className="title"><FormattedMessage id="Address" /></p>
                <Form.Item name="address">
                  <TextArea rows={4} className="textarea_form" placeholder={intl.formatMessage({ id: 'Address' })} maxLength={200} />
                </Form.Item>
              </div>
            </div>
          </div>
          <div className="establishments_form_actions">
            {isEditMode && (
              <div className="aditional_detail">
                <p><FormattedMessage id="Active" /></p>
                <Form.Item name="isActive" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
              </div>
            )}
            <div className="btn_wrapper">
              <Button className="save_btn" htmlType="submit" loading={isCreating || isUpdating}>
                <FormattedMessage id={isEditMode ? 'Save changes' : 'Save'} />
                <ArrowForward />
              </Button>
              <Button className="cancel_btn" onClick={onCloseModal}>
                <FormattedMessage id="Cancel" />
              </Button>
            </div>
          </div>
        </>
      )}
    </Form>
  );

  if (isModal) {
    return (
      <DetailModal
        className="establishments_view_modal"
        visible={isOpenModal}
        width={550}
        title={<FormattedMessage id={isEditMode ? 'Edit establishment' : 'New establishment'} />}
        onClose={onCloseModal}
        sections={[
          {
            icon: <Store />,
            title: <FormattedMessage id="Establishment information" />,
            children: (
              <div className="new_establishments_form_wrapper">
                {renderFormContent()}
              </div>
            ),
          },
        ]}
      />
    );
  }

  if (!isOpenModal) return null;

  return (
    <div className="establishments_form_fullscreen">
      <div className="establishments_form_wrapper">
        <div className="establishments_form_header">
          <h3><FormattedMessage id={isEditMode ? 'Edit establishment' : 'New establishment'} /></h3>
        </div>
        {renderFormContent()}
      </div>
    </div>
  );
};

export default EstablishmentsFormModal;
